/* QuotesCrudCard.tsx
	Une card affichant la gestion CRUD des devis clients
	Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les éléments
	Utilise l'API correspondante pour les opérations de données
*/

// Import React
import React from 'react';

// Import FontAwesome
import { faFileContract } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

// Import Communs
import { GenericCrudCard } from 'common';

// Import Feature
import { QuoteData, quotesApi } from 'features/quotes';

// Interface des props du composant
interface QuotesCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Masque le bouton retour. Utile quand la card est intégrée dans une page avec son propre bouton retour. Défaut : false */
	hideReturn?: boolean;
}

// QuotesCrudCard component
export const QuotesCrudCard: React.FC<QuotesCrudCardProps> = ({
	size = 'sm',
	returnPath = '/app/quotes',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	hideReturn = false,
}) => {
	// Formatage monétaire réutilisable
	const formatCurrency = (value: number | undefined | null): string => {
		if (value === undefined || value === null) return '—';
		return new Intl.NumberFormat('fr-FR', {
			style: 'currency',
			currency: 'EUR',
		}).format(Number(value));
	};

	const quoteIdsTitle = (a: QuoteData): string => {
		const versionId = a.latestActiveQuoteVersion?.id;
		return versionId != null
			? `ID ${a.id} / ID version ${versionId}`
			: `ID ${a.id}`;
	};

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "Référence" avec l'indice entre parenthèses et lien vers la version active
		{
			label: 'Référence (indice)',
			field: 'referenceNumber',
			render: (a: QuoteData) => {
				const indice = a.latestActiveQuoteVersion?.versionIndex;
				return indice !== undefined
					? `${a.referenceNumber} (${indice})`
					: a.referenceNumber;
			},
			linkUrl: (a: QuoteData): string => {
				if (!a.latestActiveQuoteVersion) return '';
				return `/app/quoteVersions/${a.latestActiveQuoteVersion.id}/manage`;
			},
			cellTitle: quoteIdsTitle,
			exportValue: (a: QuoteData): string => {
				const indice = a.latestActiveQuoteVersion?.versionIndex;
				return indice !== undefined
					? `${a.referenceNumber} (${indice})`
					: (a.referenceNumber ?? '');
			},
		},
		// Colonne "Libellé" = objet de la dernière version (commentIndex)
		{
			label: 'Libellé',
			field: 'none',
			render: (a: QuoteData) => a.latestActiveQuoteVersion?.commentIndex || '—',
			cellTitle: quoteIdsTitle,
			exportValue: (a: QuoteData): string =>
				a.latestActiveQuoteVersion?.commentIndex ?? '',
		},
		// Colonne "Client" avec lien vers la fiche tiers
		{
			label: 'Client',
			field: 'tiers.label',
			render: (a: QuoteData) => a.tiers?.label ?? '—',
			linkUrl: (a: QuoteData): string =>
				a.tiers ? `/app/tiers/${a.tiers.id}/manage` : '',
		},
		// Colonne "Statut" de la dernière version active
		{
			label: 'Statut',
			field: 'none',
			render: (a: QuoteData) => a.latestActiveQuoteVersion?.status ?? '—',
		},
		// Colonne "Pilote" (assignedUser) de la dernière version active — lien vers la fiche utilisateur
		{
			label: 'Pilote',
			field: 'none',
			render: (a: QuoteData) => {
				const user = a.latestActiveQuoteVersion?.assignedUser;
				if (!user) return '—';
				const fullName = [user.firstName, user.name].filter(Boolean).join(' ');
				return (
					<a
						href={`/app/users/${user.id}/edit`}
						onClick={(e) => {
							e.preventDefault();
							window.location.href = `/app/users/${user.id}/edit`;
						}}
						className="hover:underline"
					>
						{fullName || '—'}
					</a>
				);
			},
			exportValue: (a: QuoteData): string => {
				const user = a.latestActiveQuoteVersion?.assignedUser;
				if (!user) return '';
				return [user.firstName, user.name].filter(Boolean).join(' ');
			},
		},
		// Colonne "Total HT" de la dernière version active (tri et filtre désactivés : champ calculé sur la version liée)
		{
			label: 'Total HT',
			field: 'none',
			render: (a: QuoteData) =>
				formatCurrency(a.latestActiveQuoteVersion?.totalExclTax),
			exportValue: (a: QuoteData): number | string =>
				a.latestActiveQuoteVersion?.totalExclTax !== undefined
					? Number(a.latestActiveQuoteVersion.totalExclTax)
					: '',
		},
		// Colonne "Date d'édition" du devis (tri et filtre désactivés : champ calculé sur la version liée)
		{
			label: 'Éditée le',
			field: 'none',
			render: (a: QuoteData) => {
				const dateStr = a.latestActiveQuoteVersion?.editedAt;
				return dateStr ? new Date(dateStr).toLocaleDateString('fr-FR') : '—';
			},
			exportValue: (a: QuoteData): string => {
				const dateStr = a.latestActiveQuoteVersion?.editedAt;
				return dateStr ? new Date(dateStr).toLocaleDateString('fr-FR') : '';
			},
		},
	];

	// Colonnes visibles uniquement dans l'export Excel (données non affichées dans le tableau)
	const exportOnlyColumns = [
		{
			label: 'ID / ID version',
			exportValue: (a: QuoteData): string => {
				const versionId = a.latestActiveQuoteVersion?.id;
				return versionId != null ? `${a.id} / ${versionId}` : String(a.id);
			},
		},
		{
			label: 'Budgétisé',
			exportValue: (a: QuoteData): string => (a.budgeted ? 'Oui' : 'Non'),
		},
		{
			label: 'Classification',
			exportValue: (a: QuoteData): string => a.classification?.label ?? '',
		},
		{
			label: 'Exercice',
			exportValue: (a: QuoteData): number | string =>
				a.latestActiveQuoteVersion?.fiscalYear ?? '',
		},
		{
			label: 'Référence tiers',
			exportValue: (a: QuoteData): string =>
				a.latestActiveQuoteVersion?.tiersReference ?? '',
		},
		{
			label: 'Signataire',
			exportValue: (a: QuoteData): string =>
				a.latestActiveQuoteVersion?.signatory ?? '',
		},
		{
			label: 'Date de début',
			exportValue: (a: QuoteData): string => {
				const d = a.latestActiveQuoteVersion?.startDate;
				return d ? new Date(d).toLocaleDateString('fr-FR') : '';
			},
		},
		{
			label: 'Date de fin',
			exportValue: (a: QuoteData): string => {
				const d = a.latestActiveQuoteVersion?.endDate;
				return d ? new Date(d).toLocaleDateString('fr-FR') : '';
			},
		},
		{
			label: 'Début des activités',
			exportValue: (a: QuoteData): string => {
				const d = a.latestActiveQuoteVersion?.activityStartDate;
				return d ? new Date(d).toLocaleDateString('fr-FR') : '';
			},
		},
		{
			label: 'Fin des activités',
			exportValue: (a: QuoteData): string => {
				const d = a.latestActiveQuoteVersion?.activityEndDate;
				return d ? new Date(d).toLocaleDateString('fr-FR') : '';
			},
		},
		{
			label: "Chargé d'affaire",
			exportValue: (a: QuoteData): string => {
				const bm = a.latestActiveQuoteVersion?.businessManager;
				if (!bm) return '';
				return [bm.firstName, bm.lastName].filter(Boolean).join(' ');
			},
		},
	];

	return (
		<GenericCrudCard<QuoteData>
			queryKey="quotes"
			title={size === 'sm' ? 'Devis clients' : 'Liste des devis clients'}
			fetchData={(token, options) => quotesApi.listPaginated(token, options)}
			deleteItem={quotesApi.delete}
			tableColumns={columns}
			exportOnlyColumns={exportOnlyColumns}
			addButtonLabel="Ajouter un devis"
			getAddPath={() => '/app/quotes/new'}
			getManageQuoteVersionPath={(id) => `/app/quoteVersions/${id}/manage`}
			emptyMessage="Aucun devis trouvé."
			getReturnPath={hideReturn ? undefined : () => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faFileContract : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};

export default QuotesCrudCard;
